/**
 * Notices: what the person should know, from the engine (design note
 * docs/plans/2026-10-19-engine-interface-separation.md §3.2). Each has a
 * stable code an interface can rely on, a level ("info", "warning",
 * "error") and its data. Alex, 2026-10-19: the engine also gives a ready
 * message in the person's language; step 2 of the plan adds it as the
 * engine's own toasts and dialogs become notices.
 *
 * Step 1: notices come from today's events (engine/core/events.js).
 */
import { Events } from "./core/events.js";

/** The notice codes. */
export const NOTICE = Object.freeze({
  /** The server will not store changes made here (now a viewer). { mapId, offline, count } */
  CHANGES_REFUSED: "changes-refused",
  /** A change was not made: read-only, a full map, a map still loading. { action, reason } */
  EDIT_BLOCKED: "edit-blocked",
  /** The account or a map is over its storage quota. { mapId, scope } */
  QUOTA_REJECTED: "quota-rejected",
  /** The open map's size against its limit changed. { mapId, level, percent } */
  MAP_SIZE: "map-size",
  /** A shared map was taken away from this person. { mapId } */
  SHARE_REMOVED: "share-removed",
  /** A shared map's owner revoked it; a copy may have been recovered. { mapId, recoveredMapId, wasOpen } */
  SHARE_REVOKED: "share-revoked",
  /** Maps this device knew online are no longer there (moved or deleted elsewhere). { mapIds } */
  MAPS_GONE_ONLINE: "maps-gone-online",
  /** A new map was kept on this device: the server could not be reached. { mapId, title } */
  MAP_KEPT_ON_DEVICE: "map-kept-on-device",
  /** The open map's data could not be read. { mapId, mapName } */
  MAP_UNREADABLE: "map-unreadable",
  /** This device's session ended (signed out elsewhere, expired). { reason } */
  SESSION_ENDED: "session-ended",
  /** Items found without a parent were put back in the map. { mapId, count } */
  ITEMS_RECOVERED: "items-recovered",
  /** A linked map's owner revoked it. { mapId, outcome: "deferred"|"recovered"|"no-data"|"failed" } */
  LINKED_MAP_REVOKED: "linked-map-revoked",
  /** A revoked shared map was recovered only in part. { mapId } */
  SHARE_RECOVERY_INCOMPLETE: "share-recovery-incomplete",
  /** Changes could not be saved before a reload, a close or signing out (which did not happen). { before: "reload"|"close"|"sign-out" } */
  SAVE_FAILED: "save-failed",
  /** Signing out stopped: what is on the server could not be checked. */
  SIGN_OUT_CHECK_FAILED: "sign-out-check-failed",
  /** "Sign out everywhere" signed out here only: the server was not reached. */
  SIGNED_OUT_HERE_ONLY: "signed-out-here-only",
  /** A copy of changes the server refused could not be made: they stay on this device. { mapId } */
  REFUSED_COPY_FAILED: "refused-copy-failed",
  /** Files attached offline were sent. { count } */
  WAITING_FILES_SENT: "waiting-files-sent",
  /** Files attached offline were refused by the server. { count, reason } */
  WAITING_FILES_REFUSED: "waiting-files-refused",
  /** An attachment could not be downloaded or decrypted. { docId, attachmentId, name, error } */
  ATTACHMENT_FAILED: "attachment-failed",
  /** A new map was not opened: another account has a map with its id. { mapId } */
  MAP_ID_CONFLICT: "map-id-conflict",
  /** A new map was not opened: the server could not be reached. { mapId } */
  MAP_SERVER_UNREACHABLE: "map-server-unreachable",
  /** The map opened from this device's copy: the server could not be reached. { mapId } */
  MAP_SHOWING_CACHED: "map-showing-cached",
  /** The map's content was found nowhere: an empty map is shown in its place. { mapId, afterSignIn } */
  MAP_CONTENT_MISSING: "map-content-missing",
});

/**
 * Questions the engine asks the person (engine/host.js ask), with their
 * data and the answer expected.
 */
export const QUESTION = Object.freeze({
  /** Go on? { title, message, details, confirmText, cancelText } -> boolean */
  CONFIRM: "confirm",
  /** The server refused changes made here (now a viewer): { mapName, count } -> "copy" | "discard" | "later" */
  REFUSED_CHANGES: "refused-changes",
  /** Recover a linked map its owner revoked, now? { mapId, docName, context, recoverText, laterText } -> boolean */
  RECOVER_LINKED_MAP: "recover-linked-map",
});

/** Today's event -> notice (null: none). */
export const NOTICE_EVENTS = {
  // The engine's own notices (engine/host.js notify), as they are.
  [Events.ENGINE_NOTICE]: (p) => p,
  [Events.CHANGES_REFUSED]: (p) => ({ code: NOTICE.CHANGES_REFUSED, level: "warning",
    data: { mapId: p?.docId ?? null, offline: !!p?.offline, count: p?.count ?? 0 } }),
  [Events.EDIT_BLOCKED]: (p) => ({ code: NOTICE.EDIT_BLOCKED, level: "info",
    data: { action: p?.action ?? null, reason: p?.reason ?? null } }),
  [Events.QUOTA_REJECTED]: (p) => ({ code: NOTICE.QUOTA_REJECTED, level: "error",
    data: { mapId: p?.docId ?? null, scope: p?.scope ?? null } }),
  [Events.MAP_SIZE_CHANGED]: (p) => (p?.level && p.level !== "normal"
    ? { code: NOTICE.MAP_SIZE, level: p.level === "full" ? "error" : "warning",
      data: { mapId: p.docId ?? null, level: p.level, percent: p.percent ?? null } }
    : null),
  [Events.SHARE_REMOVED]: (p) => ({ code: NOTICE.SHARE_REMOVED, level: "warning", data: { mapId: p?.shareId ?? null } }),
  [Events.SHARE_REVOKED]: (p) => ({ code: NOTICE.SHARE_REVOKED, level: "warning",
    data: { mapId: p?.shareId ?? null, recoveredMapId: p?.recoveredDocId ?? null, wasOpen: !!p?.wasActive } }),
  [Events.MAPS_GONE_ONLINE]: (p) => ({ code: NOTICE.MAPS_GONE_ONLINE, level: "warning", data: { mapIds: p?.docIds || [] } }),
  [Events.MAP_KEPT_ON_DEVICE]: (p) => ({ code: NOTICE.MAP_KEPT_ON_DEVICE, level: "info",
    data: { mapId: p?.docId ?? null, title: p?.title ?? null } }),
  [Events.DOCUMENT_CORRUPTED]: (p) => ({ code: NOTICE.MAP_UNREADABLE, level: "error",
    data: { mapId: p?.docId ?? null, mapName: p?.docName ?? null } }),
  [Events.SESSION_ENDED]: (p) => ({ code: NOTICE.SESSION_ENDED, level: "warning", data: { reason: p?.reason ?? null } }),
};
